/**
 * Ship art: one hand-built top-down SVG per Shipyard hull, nose to the right,
 * on a 120×60 grid. Each ship gets its own silhouette AND a signature feature
 * you can read at a glance, the way IPM's ships each look like themselves:
 *
 *   Pathfinder  swept scout wings, sensor spike   Deepcutter    spiral drill head
 *   Ironwake    armour plates, glowing vents      Horizon       warp ring
 *   Gauntlet    twin forward prongs               Ore Barge     open hopper of ore
 *   Hauler      stacked cargo containers          Forge Tender  glowing forge dome
 *   Clipper     translucent solar sails           Vanguard      layered wings, gold trim
 *   Barque      crescent sails, gold reliquary    Sovereign     decks, bridge tower, batteries
 *
 * Shared language (IdleVoid's): a hull shaded light-top / dark-underside in
 * the ship's colour, a crisp edge, a glass canopy, panel lines, engine flames.
 * Unowned ships draw as a faint blueprint outline of the same art.
 */

import type { ShipDef } from '../content/ships';

/** Hex colour mix: t = 0 gives a, 1 gives b. */
function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (shift: number) => Math.round(((pa >> shift) & 255) * (1 - t) + ((pb >> shift) & 255) * t);
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
}

interface Pal {
  readonly lit: boolean;
  /** Hull fill (a gradient url, or none for the blueprint). */
  readonly hull: string;
  /** A second, darker plate fill. */
  readonly plate: string;
  readonly edge: string;
  /** Panel lines and shadows. */
  readonly line: string;
  readonly light: string;
  readonly accent: string;
  readonly glass: string;
  readonly gold: string;
  /** Furnace / reactor glow. */
  readonly hot: string;
  readonly flame: string;
}

const path = (d: string, fill: string, stroke: string, w = 1) =>
  `<path d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${w}" stroke-linejoin="round"/>`;

/** Mirror a path across the centre line (y = 30). */
function flip(d: string): string {
  return d.replace(/(-?\d+(?:\.\d+)?)[ ,](-?\d+(?:\.\d+)?)/g, (_, x: string, y: string) => `${x} ${60 - Number(y)}`);
}

const pair = (d: string, fill: string, stroke: string, w = 1) => path(d, fill, stroke, w) + path(flip(d), fill, stroke, w);

/** Nozzles at the stern, with an animated flame trailing left. */
function engines(P: Pal, list: readonly (readonly [number, number, number])[]): string {
  return list.map(([x, y, h]) => {
    const flame = P.lit
      ? `<ellipse cx="${x - 6}" cy="${y}" rx="8" ry="${(h * 0.42).toFixed(1)}" fill="${P.flame}" opacity="0.75" class="vm-hull-flame"/>`
        + `<ellipse cx="${x - 3}" cy="${y}" rx="4" ry="${(h * 0.22).toFixed(1)}" fill="#ffffff" opacity="0.8" class="vm-hull-flame"/>`
      : '';
    return `${flame}<rect x="${x - 1}" y="${y - h / 2}" width="5" height="${h}" rx="1.2" fill="${P.lit ? P.line : 'none'}" stroke="${P.edge}" stroke-width="0.8"/>`;
  }).join('');
}

const canopy = (P: Pal, cx: number, rx: number, ry: number) =>
  `<ellipse cx="${cx}" cy="30" rx="${rx}" ry="${ry}" fill="${P.glass}" stroke="${P.edge}" stroke-width="0.8"/>`
  + (P.lit ? `<ellipse cx="${cx + rx * 0.25}" cy="${30 - ry * 0.35}" rx="${rx * 0.45}" ry="${ry * 0.3}" fill="#ffffff" opacity="0.45"/>` : '');

const lines = (P: Pal, d: string, w = 0.7) => `<path d="${d}" fill="none" stroke="${P.line}" stroke-width="${w}" opacity="${P.lit ? 0.9 : 0.5}"/>`;

type Art = (P: Pal) => string;

const ORE_CHUNKS: readonly (readonly [number, number, string])[] = [
  [36, 26, '#c46a3a'], [44, 31, '#8fd16a'], [52, 25.5, '#d9e4f0'], [60, 32, '#c46a3a'],
  [68, 27, '#4fb8ff'], [75, 31.5, '#d9b13c'], [40, 33, '#d9e4f0'], [57, 28.5, '#8fd16a'],
];

const CONTAINERS: readonly (readonly [number, string])[] = [[26, '#4f9dff'], [44, '#f5c542'], [62, '#9b7bff'], [80, '#3be8b0']];

const ART: Readonly<Record<string, Art>> = {
  pathfinder: (P) =>
    engines(P, [[18, 30, 8]])
    + pair('M70 23 L50 5 L42 6 L54 24 Z', P.plate, P.edge)
    + path('M116 30 L92 24 L62 21 L32 23 L18 26 L18 34 L32 37 L62 39 L92 36 Z', P.hull, P.edge, 1.2)
    + lines(P, 'M40 23 L40 37 M62 21.5 L62 38.5 M32 30 L80 30')
    + pair('M52 9 L46 10', 'none', P.accent, 1.4)
    + canopy(P, 86, 8, 3.2)
    + path('M116 30 L120 30', 'none', P.accent, 1.2),

  deepcutter: (P) =>
    engines(P, [[18, 24, 7], [18, 36, 7]])
    + pair('M36 17 L44 9 L70 9 L76 17 Z', P.plate, P.edge)
    + path('M86 17 L32 15 L18 21 L18 39 L32 45 L86 43 Z', P.hull, P.edge, 1.2)
    + path('M86 18 L116 30 L86 42 Z', P.light, P.edge, 1)
    + lines(P, 'M90 19.5 L94 40.5 M96 22 L100 38 M102 24.5 L106 35.5 M108 27 L110 33', 1)
    + lines(P, 'M46 15.5 L46 44.5 M66 16 L66 44 M32 30 L64 30')
    + `<rect x="68" y="25" width="12" height="10" rx="2" fill="${P.glass}" stroke="${P.edge}" stroke-width="0.8"/>`,

  ironwake: (P) =>
    engines(P, [[16, 22, 7], [16, 30, 7], [16, 38, 7]])
    + pair('M78 19 L68 6 L42 6 L44 18 Z', P.plate, P.edge)
    + path('M106 30 L90 20 L42 16 L22 19 L16 25 L16 35 L22 41 L42 44 L90 40 Z', P.hull, P.edge, 1.3)
    + lines(P, 'M42 16.5 L42 43.5 M70 18 L70 42 M90 21 L90 39')
    + [48, 56, 64].map((x) => `<rect x="${x}" y="26" width="4" height="8" rx="1" fill="${P.hot}" opacity="${P.lit ? 0.95 : 0.3}" class="${P.lit ? 'vm-hull-glow' : ''}"/>`).join('')
    + pair('M50 8 L66 8', 'none', P.accent, 1.2)
    + canopy(P, 96, 5, 3),

  horizon_seeker: (P) =>
    engines(P, [[22, 30, 6]])
    + pair('M96 26 L114 16', 'none', P.accent, 1)
    + pair('M36 27 L28 18 L24 19 L30 28 Z', P.plate, P.edge)
    + path('M118 30 L96 26 L50 24.5 L26 27 L22 30 L26 33 L50 35.5 L96 34 Z', P.hull, P.edge, 1.1)
    + `<ellipse cx="58" cy="30" rx="7" ry="23" fill="none" stroke="${P.accent}" stroke-width="${P.lit ? 3 : 1}" opacity="${P.lit ? 0.85 : 0.6}"/>`
    + (P.lit ? `<ellipse cx="58" cy="30" rx="7" ry="23" fill="none" stroke="#ffffff" stroke-width="0.8" opacity="0.6"/>` : '')
    + lines(P, 'M40 25 L40 35 M76 25 L76 35')
    + canopy(P, 92, 7, 2.6),

  gauntlet: (P) =>
    engines(P, [[20, 25, 7], [20, 35, 7]])
    + pair('M38 22 L28 9 L23 10 L30 23 Z', P.plate, P.edge)
    + pair('M64 23 L110 18 L116 21 L70 27 Z', P.plate, P.edge, 1.1)
    + path('M82 30 L64 21 L32 21 L20 27 L20 33 L32 39 L64 39 Z', P.hull, P.edge, 1.2)
    + pair('M76 23.5 L108 19.5', 'none', P.accent, 1.2)
    + lines(P, 'M44 21.5 L44 38.5 M32 30 L56 30')
    + canopy(P, 62, 7, 3.4),

  ore_barge: (P) =>
    engines(P, [[16, 24, 7], [16, 36, 7]])
    + path('M100 21 L108 30 L100 39 L24 41 L16 36 L16 24 L24 19 Z', P.hull, P.edge, 1.2)
    + `<rect x="30" y="22" width="52" height="16" rx="2" fill="${P.lit ? P.line : 'none'}" stroke="${P.edge}" stroke-width="0.8"/>`
    + (P.lit ? ORE_CHUNKS.map(([x, y, c]) => `<polygon points="${x},${y - 2.4} ${x + 2.6},${y} ${x},${y + 2.4} ${x - 2.6},${y}" fill="${c}"/>`).join('') : lines(P, 'M30 22 L82 38 M30 38 L82 22', 0.5))
    + `<rect x="86" y="23" width="12" height="14" rx="2" fill="${P.plate}" stroke="${P.edge}" stroke-width="0.8"/>`
    + `<rect x="92" y="26" width="5" height="8" rx="1.5" fill="${P.glass}" stroke="${P.edge}" stroke-width="0.5"/>`,

  freight_hauler: (P) =>
    engines(P, [[16, 26, 6], [16, 34, 6]])
    + `<rect x="18" y="26.5" width="80" height="7" rx="1.5" fill="${P.plate}" stroke="${P.edge}" stroke-width="0.9"/>`
    + CONTAINERS.map(([x, c], i) => {
      const fill = P.lit ? mix(c, '#0a1020', 0.35) : 'none';
      const h = i % 2 ? 13 : 14;
      return `<rect x="${x}" y="${26 - h}" width="15" height="${h}" rx="1.5" fill="${fill}" stroke="${P.edge}" stroke-width="0.8"/>`
        + `<rect x="${x}" y="34" width="15" height="${27 - h}" rx="1.5" fill="${fill}" stroke="${P.edge}" stroke-width="0.8"/>`
        + lines(P, `M${x + 5} ${27 - h} L${x + 5} 25 M${x + 10} ${27 - h} L${x + 10} 25 M${x + 5} 35 L${x + 5} ${60 - h} M${x + 10} 35 L${x + 10} ${60 - h}`, 0.5);
    }).join('')
    + path('M98 21 L104 21 L114 30 L104 39 L98 39 Z', P.hull, P.edge, 1.1)
    + canopy(P, 106, 4, 3.4),

  forge_tender: (P) =>
    engines(P, [[20, 23, 6], [20, 30, 6], [20, 37, 6]])
    + pair('M72 21 L84 8 L88 9 L78 22', 'none', P.accent, 1.6)
    + pair('M30 22 L38 14 L52 14 L52 22 Z', P.plate, P.edge)
    + path('M102 30 L88 21 L36 20 L22 24 L22 36 L36 40 L88 39 Z', P.hull, P.edge, 1.2)
    + `<circle cx="58" cy="30" r="11" fill="${P.lit ? 'url(#hg-forge-hot)' : 'none'}" stroke="${P.edge}" stroke-width="1"/>`
    + `<circle cx="58" cy="30" r="6" fill="${P.hot}" opacity="${P.lit ? 0.9 : 0.25}" class="${P.lit ? 'vm-hull-glow' : ''}"/>`
    + lines(P, 'M36 20.5 L36 39.5 M78 21 L78 39')
    + canopy(P, 92, 5, 3),

  trade_clipper: (P) =>
    engines(P, [[26, 30, 6]])
    + pair('M72 25 L58 3 L40 5 L52 25 Z', P.lit ? P.accent : 'none', P.accent, 0.8).replace(/<path /g, `<path fill-opacity="${P.lit ? 0.28 : 0}" `)
    + pair('M70 24 L57 6 M64 24.5 L52 7 M58 25 L47 8', 'none', P.lit ? '#ffffff' : P.edge, 0.4)
    + path('M118 30 L96 26 L44 25 L28 28 L26 30 L28 32 L44 35 L96 34 Z', P.hull, P.edge, 1.1)
    + lines(P, 'M44 25.5 L44 34.5 M80 25.5 L80 34.5')
    + canopy(P, 98, 8, 2.6),

  vanguard: (P) =>
    engines(P, [[18, 25, 7], [18, 35, 7]])
    + pair('M72 21 L56 3 L36 6 L46 21 Z', P.plate, P.edge)
    + pair('M62 21 L50 11 L42 12 L48 21 Z', P.hull, P.edge, 0.8)
    + path('M114 30 L94 22 L56 19 L28 21 L18 26 L18 34 L28 39 L56 41 L94 38 Z', P.hull, P.edge, 1.3)
    + pair('M58 6 L40 8', 'none', P.gold, 1.2)
    + `<path d="M28 30 L84 30" stroke="${P.gold}" stroke-width="1" opacity="${P.lit ? 0.9 : 0.4}"/>`
    + lines(P, 'M44 21 L44 39 M70 20 L70 40')
    + canopy(P, 92, 8, 3.4),

  tithe_barque: (P) =>
    engines(P, [[20, 25, 7], [20, 35, 7]])
    + pair('M74 22 C62 2 36 2 24 9 C40 9 52 14 60 22 Z', P.plate, P.edge)
    + pair('M70 20 C60 6 42 5 32 9', 'none', P.gold, 1)
    + path('M110 30 L94 24 L60 20 L30 22 L20 27 L20 33 L30 38 L60 40 L94 36 Z', P.hull, P.edge, 1.3)
    + `<polygon points="58,22 66,30 58,38 50,30" fill="${P.lit ? P.gold : 'none'}" stroke="${P.lit ? '#fff3c4' : P.edge}" stroke-width="0.9" class="${P.lit ? 'vm-hull-glow' : ''}"/>`
    + lines(P, 'M40 21.5 L40 38.5 M78 21.5 L78 38.5')
    + canopy(P, 96, 6, 3),

  sovereign: (P) =>
    engines(P, [[14, 22, 7], [14, 30, 8], [14, 38, 7]])
    + pair('M82 16 L72 3 L40 3 L44 15 Z', P.plate, P.edge)
    + pair('M66 6 L72 6 M56 6 L62 6 M46 6 L52 6', 'none', P.accent, 1.6)
    + path('M118 30 L100 22 L82 16 L36 14 L16 21 L14 30 L16 39 L36 46 L82 44 L100 38 Z', P.hull, P.edge, 1.4)
    + path('M90 25 L106 30 L90 35 L54 36 L54 24 Z', P.plate, P.edge, 0.9)
    + pair('M36 16 L82 18', 'none', P.gold, 0.9)
    + lines(P, 'M36 14.5 L36 45.5 M48 14.5 L48 45.5')
    + `<rect x="62" y="26.5" width="16" height="7" rx="1.5" fill="${P.lit ? P.line : 'none'}" stroke="${P.edge}" stroke-width="0.8"/>`
    + [65, 69, 73].map((x) => `<rect x="${x}" y="28.5" width="2.4" height="3" fill="${P.lit ? '#bfeaff' : 'none'}"/>`).join('')
    + canopy(P, 98, 5, 2.6),
};

function palette(def: ShipDef, lit: boolean): Pal {
  const c = def.color;
  if (!lit) {
    const faint = 'rgba(160,180,220,0.3)';
    return { lit, hull: 'none', plate: 'none', edge: faint, line: faint, light: 'none', accent: faint, glass: 'none', gold: faint, hot: faint, flame: 'none' };
  }
  return {
    lit,
    hull: `url(#hg-${def.id})`,
    plate: mix(c, '#0a1020', 0.62),
    edge: mix(c, '#ffffff', 0.25),
    line: mix(c, '#05080f', 0.72),
    light: mix(c, '#ffffff', 0.15),
    accent: c,
    glass: 'url(#hg-glass)',
    gold: '#f5c542',
    hot: '#ff9a3c',
    flame: mix(c, '#ffffff', 0.35),
  };
}

function defs(def: ShipDef): string {
  const c = def.color;
  return `<defs>
    <linearGradient id="hg-${def.id}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${mix(c, '#ffffff', 0.12)}"/>
      <stop offset="45%" stop-color="${mix(c, '#0a1020', 0.45)}"/>
      <stop offset="100%" stop-color="${mix(c, '#05080f', 0.8)}"/>
    </linearGradient>
    <linearGradient id="hg-glass" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#d8f6ff"/><stop offset="100%" stop-color="#2a6f9a"/></linearGradient>
    <radialGradient id="hg-forge-hot"><stop offset="0%" stop-color="#ffe0a0"/><stop offset="60%" stop-color="#ff7a2a"/><stop offset="100%" stop-color="#5a1a08"/></radialGradient>
  </defs>`;
}

/**
 * The ship as inline SVG. `lit` = owned (full colour) vs a faint blueprint.
 * `place` positions it inside a parent SVG (centred on x, y, `w` wide).
 */
export function hullSvg(def: ShipDef, lit: boolean, place?: { x: number; y: number; w: number }): string {
  const art = ART[def.id];
  const body = art ? art(palette(def, lit)) : '';
  const at = place ? ` x="${place.x - place.w / 2}" y="${place.y - place.w / 4}" width="${place.w}" height="${place.w / 2}"` : '';
  return `<svg class="vm-hull${lit ? ' lit' : ''}"${at} viewBox="0 0 120 60" aria-hidden="true" style="--hull:${def.color}">${lit ? defs(def) : ''}${body}</svg>`;
}
